/**
 * The eleven groups the Rulebook is read in, and the one predicate that decides membership.
 *
 * THE BUG THIS FILE EXISTS TO PREVENT is a badge that disagrees with its pane. The design pass
 * before this one assigned each rule to a single group by priority, and 2020-II-1 — which defines
 * how draft slots are chosen — never appeared under Draft, because it is tagged `draft` AND
 * `playoffs` and playoffs won the coin toss. Membership here is multi-valued: `rulesIn` is the
 * only thing that decides what a pane holds, `countIn` is `rulesIn().length`, and there is no way
 * to write a count down by hand.
 *
 * THE GROUPS ARE NOT THE TOPICS. Thirteen topics in the data map onto ten rule groups, and three
 * of those groups are unions:
 *
 *   FAAB and wagering      = faab ∪ wagering                      16
 *   Roster and IR          = roster ∪ ir                          12
 *   Governance and voting  = governance ∪ commissioner ∪ fines    31
 *
 * Every count in the handoff table reproduces exactly from these unions, which is how the unions
 * were recovered — `fines` belongs to Governance rather than to FAAB, and `commissioner` is not a
 * group of its own. `tests/ruleGroups.test.ts` re-derives all ten from the data and fails if the
 * shipped rulebook drifts from them.
 */

import { RULES, type RulebookEntry, ruleEntry } from './rulebook.js';

/** A group renders as a list of rules, the keeper Grid, or the scoring table. */
export type GroupKind = 'list' | 'grid' | 'scoring';

export interface RuleGroup {
  id: string;
  label: string;
  /** One line, in the language a manager would use to say what he came looking for. */
  blurb: string;
  /**
   * The four lead groups carry an identity hue; the second tier is plain. `null` rather than a
   * default colour, so "has no dot" is a stated fact rather than an accident of the palette.
   */
  hue: number | null;
  kind: GroupKind;
  /** The topics whose union IS this group. Empty for the Grid, which is a table, not a query. */
  topics: readonly string[];
}

export const RULE_GROUPS: readonly RuleGroup[] = [
  {
    id: 'grid',
    label: 'The Grid',
    blurb: 'What a keeper costs you, by the round he was drafted in',
    hue: 150,
    kind: 'grid',
    // No topics: the Grid is 2019-II-1 rendered as a table. It has no list to count.
    topics: [],
  },
  {
    id: 'keepers',
    label: 'Keepers',
    blurb: 'Who you may keep, for how long, and what the tax comes to',
    hue: 232,
    kind: 'list',
    topics: ['keepers'],
  },
  {
    id: 'farm-system',
    label: 'Farm system',
    blurb: 'College players, the auction, and what happens when one busts',
    hue: 95,
    kind: 'list',
    topics: ['farm-system'],
  },
  {
    id: 'draft',
    label: 'Draft and lottery',
    blurb: 'How the order is set, and how draft night runs',
    hue: 35,
    kind: 'list',
    topics: ['draft'],
  },
  {
    id: 'trades',
    label: 'Trades',
    blurb: 'Windows, counters, vetoes and what moves with a player',
    hue: null,
    kind: 'list',
    topics: ['trades'],
  },
  {
    id: 'faab',
    label: 'FAAB and wagering',
    blurb: 'The scrip, what buys it, and what it can be spent on',
    hue: null,
    kind: 'list',
    topics: ['faab', 'wagering'],
  },
  {
    id: 'roster',
    label: 'Roster and IR',
    blurb: 'Roster limits, injured reserve, and lineup obligations',
    hue: null,
    kind: 'list',
    topics: ['roster', 'ir'],
  },
  {
    id: 'scoring',
    label: 'Scoring',
    blurb: 'What the league scores, and where it differs from Yahoo',
    hue: null,
    kind: 'scoring',
    topics: ['scoring'],
  },
  {
    id: 'playoffs',
    label: 'Playoffs and the Toilet Bowl',
    blurb: 'Who makes it, how it seeds, and what last place is worth',
    hue: null,
    kind: 'list',
    topics: ['playoffs'],
  },
  {
    id: 'governance',
    label: 'Governance and voting',
    blurb: 'The Competition Committee, the commissioners, and fines',
    hue: null,
    kind: 'list',
    topics: ['governance', 'commissioner', 'fines'],
  },
];

const BY_ID = new Map(RULE_GROUPS.map((g) => [g.id, g]));

export const ruleGroup = (id: string): RuleGroup | undefined => BY_ID.get(id);

/** The four with a dot, in the order the rail draws them. */
export const LEAD_GROUPS: readonly RuleGroup[] = RULE_GROUPS.filter((g) => g.hue !== null);
export const OTHER_GROUPS: readonly RuleGroup[] = RULE_GROUPS.filter((g) => g.hue === null);

/**
 * THE ONE MEMBERSHIP TEST. Everything — the pane, the badge, which group a `§` selects — goes
 * through here, so a count cannot disagree with what it counts.
 */
export function inGroup(rule: Pick<RulebookEntry, 'topics'>, group: RuleGroup): boolean {
  const topics = rule.topics ?? [];
  return group.topics.some((t) => topics.includes(t));
}

export interface RulesInOptions {
  /**
   * Rules no longer in force. Off by default — a group's pane and its badge are both "what is in
   * force now" — but a superseded rule is still reachable, because `§` links and lineage lines
   * point at them. See `groupsFor`.
   */
  includeRetired?: boolean;
  /** Narrow to a single year. The sub-line reads "6 of 25 in force · 2019". */
  year?: number | 'all';
}

/**
 * FAILED PROPOSALS ARE NOT RULES and never appear, at any setting. They are kept in the data so
 * the league can see what was considered and rejected, and `rulebook.json`'s own note says every
 * display must filter them out.
 */
const isLaw = (r: RulebookEntry): boolean => r.status !== 'failed-proposal';
const inForce = (r: RulebookEntry): boolean => r.status === 'active';

export function rulesIn(groupId: string, opts: RulesInOptions = {}): RulebookEntry[] {
  const group = BY_ID.get(groupId);
  if (!group || group.topics.length === 0) return [];
  const year = opts.year ?? 'all';
  return RULES
    .filter(isLaw)
    .filter((r) => (opts.includeRetired ? true : inForce(r)))
    .filter((r) => inGroup(r, group))
    .filter((r) => (year === 'all' ? true : r.year === year))
    .sort((a, b) => (b.year - a.year) || a.id.localeCompare(b.id));
}

/** The badge. Same predicate, no arguments, no literal anywhere. */
export const countIn = (groupId: string): number => rulesIn(groupId).length;

/**
 * Every group a rule belongs to — plural on purpose. 48 of the 84 in-force rules carry more than
 * one topic, and each of them appears in every group its topics reach.
 */
export function groupsFor(ruleId: string): RuleGroup[] {
  const rule = ruleEntry(ruleId);
  if (!rule || !isLaw(rule)) return [];
  return RULE_GROUPS.filter((g) => g.topics.length > 0 && inGroup(rule, g));
}

/**
 * The group to select when somebody arrives on `#2015-II-2`.
 *
 * The FIRST group that holds it, in rail order, so a rule tagged `draft` and `playoffs` opens
 * under Draft — the tier-one group — rather than wherever `Object.keys` happened to land.
 *
 * Superseded rules resolve too, and that is not a nicety. Three of the twenty-odd `§` ids reserved
 * by earlier passes point at superseded rules, and one of them — 2020-I-5, which carries the ₣50
 * rename-refusal fine and the deficit-carries-forward clause — was superseded by an amendment that
 * only lowered a blowout threshold from 100 to 75 and restated nothing else. The operative text
 * lives in the superseded rule. A rulebook that cannot open one cannot answer the question.
 */
export function groupOwning(ruleId: string): RuleGroup | null {
  return groupsFor(ruleId)[0] ?? null;
}

export interface Lineage {
  /** What this rule replaced, oldest first. Straight from `supersedes` — never hand-authored. */
  replaces: RulebookEntry[];
  /** What replaced it, if anything. */
  replacedBy: RulebookEntry[];
}

export function lineageOf(ruleId: string): Lineage {
  const rule = ruleEntry(ruleId);
  if (!rule) return { replaces: [], replacedBy: [] };
  const look = (ids: readonly string[] | undefined) => (ids ?? [])
    .map((id) => ruleEntry(id))
    .filter((r): r is RulebookEntry => r !== undefined)
    .sort((a, b) => a.year - b.year);
  return { replaces: look(rule.supersedes), replacedBy: look(rule.supersededBy) };
}

/** Years a group has rules in, newest first, for the year filter at the foot of the rail. */
export function yearsIn(groupId: string): number[] {
  return [...new Set(rulesIn(groupId, { year: 'all' }).map((r) => r.year))]
    .sort((a, b) => b - a);
}
